import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import moment from 'moment';
import careTeamApi, { errorText } from './careTeamApi';
import PersonPicker from './PersonPicker';
import UndoToast from './UndoToast';
import { CHOOSE_PLACEHOLDERS, assignConfirmText, removeConfirmText, sameForBothText } from './careTeamText';

// Patient profile Care Team: RPM nurse / CCM nurse / Provider with inline Save / Remove, confirm and 10-second Undo.
const CareTeamPanel = ({ patientId, onChanged }) => {
    const [team, setTeam] = useState(null);
    const [error, setError] = useState('');
    const [picks, setPicks] = useState({});
    const [outside, setOutside] = useState({});
    const [confirm, setConfirm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);
    const [showHistory, setShowHistory] = useState(false);

    const latest = useRef(0);
    const load = useCallback(() => {
        if (!patientId) return;
        const req = ++latest.current;
        careTeamApi.patient(patientId)
            .then((t) => { if (req === latest.current) { setTeam(t); setError(''); } })
            .catch((e) => { if (req === latest.current) setError(errorText(e)); });
    }, [patientId]);
    // Fresh names on mount, after every save/undo, and when the tab regains focus (another user may have changed them).
    useEffect(() => {
        load();
        const onVisible = () => { if (document.visibilityState === 'visible') load(); };
        window.addEventListener('focus', load);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.removeEventListener('focus', load);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [load]);

    const closeToast = useCallback(() => setToast(null), []);

    if (error) return <div className="alert alert-warning py-2" data-testid="care-team-panel">Care team: {error}</div>;
    if (!team) return <div className="card p-2 mb-3" data-testid="care-team-panel"><small>Loading care team…</small></div>;

    const patientName = team.patient.name;
    const slot = (key) => team.slots.find((s) => s.slot === key);
    const peopleFor = (key) => (key === 'provider' ? team.providers : team.nurses);
    const nameOf = (key, id) => (peopleFor(key).find((p) => p.id === id) || {}).name;
    const bothInPrograms = slot('rpm').inProgram && slot('ccm').inProgram;

    const run = async (body, message) => {
        setSaving(true);
        try {
            const res = await careTeamApi.apply({ ...body, patientIds: [patientId], source: 'patient' });
            const failed = (res.results || []).find((r) => r.status !== 'ok');
            if (failed) setError(failed.reason || 'Could not save');
            else if (res.summary?.changed) setToast({ message, batchId: res.batchId });
            setPicks({});
            load();
            if (onChanged) onChanged();
        } catch (e) {
            setToast({ message: errorText(e) });
        } finally {
            setSaving(false);
            setConfirm(null);
        }
    };

    const askSave = (key) => {
        const s = slot(key);
        const toId = picks[key];
        const toName = nameOf(key, toId);
        setConfirm({
            text: assignConfirmText({ fromName: s.current?.name, toName, slotLabel: s.label, patientName }),
            body: { slot: key, personId: toId, allowOutsideProgram: !s.inProgram },
            message: `${s.label} set to ${toName}`,
        });
    };
    const askRemove = (key) => {
        const s = slot(key);
        setConfirm({
            text: removeConfirmText({ fromName: s.current.name, slotLabel: s.label, patientName }),
            body: { slot: key, personId: null },
            message: `${s.current.name} removed as ${s.label}`,
            danger: true,
        });
    };
    const sameNurseId = picks.rpm || picks.ccm || slot('rpm').current?.id || slot('ccm').current?.id;
    const askSame = () => {
        const toName = nameOf('rpm', sameNurseId) || slot('rpm').current?.name || slot('ccm').current?.name;
        setConfirm({
            text: sameForBothText({ toName, rpm: slot('rpm').current, ccm: slot('ccm').current, patientName }),
            body: { slot: 'both', personId: sameNurseId, allowOutsideProgram: !bothInPrograms },
            message: `${toName} is now RPM and CCM nurse`,
        });
    };
    const undo = async (batchId) => {
        setToast(null);
        try {
            await careTeamApi.undo(batchId);
            setToast({ message: 'Change undone' });
            load();
            if (onChanged) onChanged();
        } catch (e) {
            setToast({ message: errorText(e) });
        }
    };

    const sameIsNoop = slot('rpm').current?.id === sameNurseId && slot('ccm').current?.id === sameNurseId;

    return (
        <div className="card p-3 mb-3" data-testid="care-team-panel" style={{ borderLeft: '4px solid #004aad' }}>
            <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-2" style={{ color: '#004aad' }}><i className="bx bx-group"></i> <strong>Care Team</strong></h6>
                {!team.canEdit && <small className="text-muted">View only</small>}
            </div>
            {team.slots.map((s) => {
                const hidden = s.program && !s.inProgram && !s.current && !outside[s.slot];
                return (
                    <div key={s.slot} className="row align-items-center py-1" data-testid={`care-team-row-${s.slot}`} style={{ borderTop: '1px solid #eee' }}>
                        <div className="col-md-2"><strong style={{ fontSize: '14px' }}>{s.label}</strong></div>
                        <div className="col-md-3" style={{ fontSize: '14px' }}>
                            {s.current ? s.current.name : <span className="text-muted">Not assigned</span>}
                            {s.program && !s.inProgram && (
                                <div><small className="text-muted">Patient is not in {s.program}.</small></div>
                            )}
                        </div>
                        {team.canEdit && (hidden ? (
                            <div className="col-md-7">
                                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setOutside({ ...outside, [s.slot]: true })}>
                                    Assign a {s.label} anyway
                                </button>
                            </div>
                        ) : (
                            <div className="col-md-7 d-flex align-items-center" style={{ gap: '6px' }}>
                                <PersonPicker
                                    id={`care-team-${s.slot}`}
                                    label={s.label}
                                    people={peopleFor(s.slot)}
                                    placeholder={CHOOSE_PLACEHOLDERS[s.slot]}
                                    value={picks[s.slot]}
                                    onChange={(v) => setPicks({ ...picks, [s.slot]: v })}
                                    disabled={saving}
                                />
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    disabled={saving || !picks[s.slot] || picks[s.slot] === s.current?.id}
                                    onClick={() => askSave(s.slot)}
                                >Save</button>
                                {s.current && (
                                    <button type="button" className="btn btn-outline-danger btn-sm" disabled={saving} onClick={() => askRemove(s.slot)}>
                                        Remove
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                );
            })}
            {team.canEdit && (
                <div className="d-flex justify-content-between align-items-center mt-2">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        disabled={saving || !sameNurseId || sameIsNoop}
                        onClick={askSame}
                        title="Use one nurse for both the RPM and the CCM slot"
                    >Same nurse for both</button>
                    {team.history?.length > 0 && (
                        <button type="button" className="btn btn-link btn-sm" onClick={() => setShowHistory(!showHistory)}>
                            {showHistory ? 'Hide' : 'Show'} recent changes
                        </button>
                    )}
                </div>
            )}
            {showHistory && (
                <ul className="mt-2 mb-0" style={{ fontSize: '12px' }} data-testid="care-team-history">
                    {team.history.map((h) => (
                        <li key={h._id}>
                            {moment(h.createdAt).format('MM/DD/YYYY hh:mm A')} — {h.actorName}: {h.action === 'undo' ? 'undo, ' : ''}{h.action === 'correction' ? 'correction, ' : ''}
                            {h.slot === 'provider' ? 'Provider' : `${h.slot.toUpperCase()} nurse`} {h.fromName || 'Not assigned'} → {h.toName || 'Not assigned'}{h.action === 'correction' && h.note ? ` — ${h.note}` : ''}
                        </li>
                    ))}
                </ul>
            )}

            <Modal show={!!confirm} onHide={() => setConfirm(null)} animation={false} aria-labelledby="care-team-confirm-title">
                <Modal.Header closeButton>
                    <Modal.Title id="care-team-confirm-title" as="h6">Confirm care team change</Modal.Title>
                </Modal.Header>
                <Modal.Body><p data-testid="care-team-confirm-text" style={{ fontSize: '14px' }}>{confirm?.text}</p></Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => setConfirm(null)}>Cancel</button>
                    <button
                        type="button"
                        className={`btn btn-sm ${confirm?.danger ? 'btn-danger' : 'btn-primary'}`}
                        disabled={saving}
                        onClick={() => run(confirm.body, confirm.message)}
                    >{confirm?.danger ? 'Remove' : 'Confirm'}</button>
                </Modal.Footer>
            </Modal>
            <UndoToast toast={toast} onUndo={undo} onClose={closeToast} />
        </div>
    );
};

export default CareTeamPanel;
